import React from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Calendar from './components/Calendar';
import UpcomingPaydays from './components/UpcomingPaydays';
import VacationPlanner from './components/VacationPlanner';
import DebtSnowball from './components/DebtSnowball';
import Subscriptions from './components/Subscriptions';
import Admin from './components/Admin';
import AdminSupport from './components/AdminSupport';
import Support from './components/Support';
import SavingsTracker from './components/SavingsTracker';
import Resources from './components/Resources';
import Donate from './components/Donate';
import CanIAffordIt from './components/CanIAffordIt';
import UserSettings from './components/UserSettings';
import LandingPage from './components/LandingPage';
import NewUserSetup from './components/NewUserSetup';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import BroadcastMessage from './components/BroadcastMessage';
import ImpersonationBanner from './components/ImpersonationBanner';
import { useTheme } from './contexts/ThemeContext';
import SpendingAnalytics from './components/SpendingAnalytics';
import BudgetBuckets from './components/BudgetBuckets';
import MobileBuckets from './components/MobileBuckets';
import BucketSpendingReport from './components/BucketSpendingReport';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './lib/firebase';

export default function App() {
  const [currentView, setCurrentView] = React.useState('calendar');
  const { user, loading, unreadBroadcast, markBroadcastAsSeen, impersonationData } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Check for first login
  React.useEffect(() => {
    const checkFirstLogin = async () => {
      if (!user) return;

      // Check if returning from impersonation
      const returnToAdmin = localStorage.getItem('returnToAdmin');
      if (returnToAdmin === 'true') {
        localStorage.removeItem('returnToAdmin');
        setCurrentView('admin');
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (!userData.hasLoggedIn) {
          // Update user document to mark first login
          await updateDoc(userRef, {
            hasLoggedIn: true,
            firstLoginDate: Timestamp.now()
          });

          // Set view to support and navigate
          setCurrentView('support');
        }
      }
    };

    checkFirstLogin();
  }, [user]);

  // Handle view changes
  const handleViewChange = (view: string) => {
    setCurrentView(view);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  const renderMainContent = () => (
    <div className={`min-h-screen flex ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      <Navbar onViewChange={handleViewChange} currentView={currentView} />
      <div className="flex-1 flex flex-col">
        {impersonationData && (
          <ImpersonationBanner
            adminEmail={impersonationData.adminEmail}
            targetEmail={impersonationData.targetEmail}
          />
        )}
        <main className="flex-1 p-6">
          <ErrorBoundary>
            {location.pathname === '/spending-report' ? (
              <BucketSpendingReport />
            ) : (
              <>
                {currentView === 'calendar' && <Calendar />}
                {currentView === 'upcoming-paydays' && <UpcomingPaydays />}
                {currentView === 'savings' && <SavingsTracker />}
                {currentView === 'analytics' && <SpendingAnalytics />}
                {currentView === 'buckets' && <BudgetBuckets />}
                {currentView === 'affordit' && <CanIAffordIt />}
                {currentView === 'vacation' && <VacationPlanner />}
                {currentView === 'debt' && <DebtSnowball />}
                {currentView === 'subscriptions' && <Subscriptions />}
                {currentView === 'support' && (user.isAdmin ? <AdminSupport /> : <Support />)}
                {currentView === 'resources' && <Resources />}
                {currentView === 'donate' && <Donate />}
                {currentView === 'admin' && user.isAdmin && <Admin />}
                {currentView === 'settings' && <UserSettings />}
              </>
            )}
          </ErrorBoundary>
        </main>
        {unreadBroadcast && (
          <BroadcastMessage
            message={unreadBroadcast}
            onClose={() => markBroadcastAsSeen(unreadBroadcast.id)}
          />
        )}
        <footer className="bg-gray-800 dark:bg-gray-900 text-white py-4 mt-auto">
          <div className="px-4 sm:px-6 lg:px-8 flex justify-center">
            <span>Powered by Tanic Software © 2024</span>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <Routes>
      <Route path="/newuser" element={<NewUserSetup />} />
      <Route path="/m" element={<MobileBuckets />} />
      <Route path="/spending-report" element={renderMainContent()} />
      <Route path="*" element={renderMainContent()} />
    </Routes>
  );
}